/**
 * DistrictSidebar (v6.32.102) — Left-rail navigation for the District
 * Admin Dashboard. Mirrors the v6.32.99 SuperAdminSidebar pattern but
 * scaled down for the district admin's narrower scope.
 *
 * 8 tabs grouped into 3 task-named sections (Performance · People ·
 * Operations). Same UX primitives as Super Admin and Teacher
 * sidebars.
 */
import React, { useState, useMemo, useEffect } from 'react';
import {
  BarChart3,
  Building2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  CreditCard,
  GraduationCap,
  Home,
  Layers,
  Menu,
  School,
  Search,
  TrendingUp,
  Users,
  Wrench,
  X,
} from 'lucide-react';

export const DISTRICT_TAB_GROUPS = [
  {
    id: 'performance',
    label: 'Performance',
    icon: BarChart3,
    tabs: [
      { value: 'overview', label: 'Overview', icon: Home, testId: 'da-overview-tab' },
      { value: 'school-performance', label: 'School Performance', icon: School, testId: 'school-performance-tab' },
      { value: 'modality', label: 'Modality', icon: Layers, testId: 'modality-tab' },
      { value: 'by-grade', label: 'By Grade', icon: GraduationCap, testId: 'by-grade-tab' },
      { value: 'wida', label: 'Proficiency', icon: TrendingUp, testId: 'da-wida-tab' },
    ],
  },
  {
    id: 'people',
    label: 'People',
    icon: Users,
    tabs: [
      { value: 'users', label: 'Users', icon: Users, testId: 'user-management-tab' },
      { value: 'assignments', label: 'Assignments', icon: ClipboardCheck, testId: 'assignments-tab' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: Wrench,
    tabs: [
      { value: 'licenses', label: 'Licenses', icon: CreditCard, testId: 'licenses-tab' },
    ],
  },
];

const findGroupForTab = (tabValue) => {
  for (const group of DISTRICT_TAB_GROUPS) {
    if (group.tabs.some((t) => t.value === tabValue)) return group.id;
  }
  return DISTRICT_TAB_GROUPS[0].id;
};

const DistrictSidebar = ({ activeTab, onTabChange }) => {
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState(() => ({
    [findGroupForTab(activeTab)]: true,
  }));
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const groupId = findGroupForTab(activeTab);
    setOpenGroups((prev) => (prev[groupId] ? prev : { ...prev, [groupId]: true }));
  }, [activeTab]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return DISTRICT_TAB_GROUPS;
    return DISTRICT_TAB_GROUPS
      .map((group) => {
        const matchedTabs = group.tabs.filter(
          (t) =>
            t.label.toLowerCase().includes(q) ||
            t.value.toLowerCase().includes(q) ||
            group.label.toLowerCase().includes(q),
        );
        return { ...group, tabs: matchedTabs };
      })
      .filter((group) => group.tabs.length > 0);
  }, [search]);

  const effectiveOpenGroups = useMemo(() => {
    if (!search.trim()) return openGroups;
    const all = {};
    filteredGroups.forEach((g) => { all[g.id] = true; });
    return all;
  }, [search, openGroups, filteredGroups]);

  const handleTabClick = (tabValue) => {
    onTabChange(tabValue);
    setMobileOpen(false);
  };

  const toggleGroup = (groupId) => {
    setOpenGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const sidebarContent = (
    <>
      <div className="px-4 pt-4 pb-3 border-b border-gray-200">
        <div className="relative" data-testid="da-sidebar-search-wrap">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tabs…"
            aria-label="Search District Dashboard tabs"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#002FA7] focus:outline-none transition-colors"
            data-testid="da-sidebar-search"
          />
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-2" aria-label="District Dashboard navigation">
        {filteredGroups.length === 0 && (
          <div className="px-4 py-6 text-sm text-gray-500 text-center" data-testid="da-sidebar-no-results">
            No tabs match "<span className="font-mono">{search}</span>"
          </div>
        )}
        {filteredGroups.map((group) => {
          const GroupIcon = group.icon;
          const isOpen = !!effectiveOpenGroups[group.id];
          return (
            <div key={group.id} className="mb-1">
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                className="w-full flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                aria-expanded={isOpen}
                data-testid={`da-sidebar-group-${group.id}`}
              >
                <GroupIcon size={14} />
                <span className="flex-1 text-left">{group.label}</span>
                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {isOpen && (
                <ul className="mt-0.5 mb-2">
                  {group.tabs.map((tab) => {
                    const TabIcon = tab.icon;
                    const isActive = activeTab === tab.value;
                    return (
                      <li key={tab.value}>
                        <button
                          type="button"
                          onClick={() => handleTabClick(tab.value)}
                          className={`w-full flex items-center gap-2 pl-10 pr-4 py-2 text-sm transition-colors border-l-2 ${
                            isActive
                              ? 'bg-blue-50 text-[#002FA7] font-semibold border-[#002FA7]'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-transparent'
                          }`}
                          aria-current={isActive ? 'page' : undefined}
                          data-testid={tab.testId}
                        >
                          <TabIcon size={14} className="flex-shrink-0" />
                          <span className="text-left">{tab.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </nav>
    </>
  );

  return (
    <>
      {/* v6.34.14 — Top-left hamburger (primary mobile trigger, visible <768px) */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-3 left-3 z-30 flex items-center justify-center w-11 h-11 bg-[#002FA7] text-white shadow-lg rounded-lg"
        aria-label="Open District Dashboard navigation"
        data-testid="da-sidebar-mobile-open-top"
      >
        <Menu size={22} />
      </button>

      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed bottom-6 right-6 z-30 flex items-center gap-2 px-4 py-3 bg-[#002FA7] text-white shadow-lg rounded-full"
        aria-label="Open District Dashboard navigation"
        data-testid="da-sidebar-mobile-open"
      >
        <Menu size={18} />
        <span className="text-sm font-semibold">Menu</span>
      </button>

      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`md:hidden fixed top-0 left-0 bottom-0 w-72 bg-white z-50 shadow-xl transition-transform duration-200 flex flex-col ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="da-sidebar-mobile"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <span className="text-sm font-bold uppercase tracking-wide text-[#002FA7]">District</span>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="p-1 hover:bg-gray-100 rounded"
            aria-label="Close navigation"
            data-testid="da-sidebar-mobile-close"
          >
            <X size={18} />
          </button>
        </div>
        {sidebarContent}
      </aside>

      <aside
        className="hidden md:flex flex-col w-60 flex-shrink-0 bg-white border-r border-gray-200 sticky top-0 self-start"
        style={{ height: 'calc(100vh - 4rem)' }}
        data-testid="da-sidebar-desktop"
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default DistrictSidebar;
